import React, { useCallback, useEffect, useState } from 'react';
import type { DailyRoutine } from '../types/routine';
import { RoutineService } from '../services/routineService';
import RoutineView from './RoutineView';
import RoutineEditor from './RoutineEditor';
import type { ImportedDraftInput } from './RoutineEditor';
import ImportRoutineModal from './ImportRoutineModal';
import WorkoutView from '../../workouts/components/WorkoutView';
import { WorkoutsService } from '../../workouts/workoutsService';
import type { WorkoutSession } from '../../workouts/types';
import { useMiSemana } from '../hooks/useMiSemana';
import WeekdaySelector from './WeekdaySelector';
import {
  NOMBRE_DEL_DIA,
  ORDEN_DE_LA_SEMANA,
  diaDeHoy,
  numeroDelDia,
} from '../utils/semana';
import { AlertIcon } from '../../../assets/icons/index.tsx';

/**
 * Mi semana: hasta siete rutinas propias, una por día.
 *
 * Las armas una vez, las completas o no, y al empezar la semana siguiente
 * vuelven a estar por hacer. «Volver a estar por hacer» no borra nada: lo
 * hecho vive en las sesiones de entrenamiento, con su fecha, y la ventana de
 * la semana se corre sola.
 *
 * Una rutina puede quedarse sin día (`dayOfWeek` a null). Sigue guardada, solo
 * que no ocupa hueco en la semana — es donde van a parar las que se clonan de
 * la biblioteca hasta que decides cuándo hacerlas.
 */
const MyRoutinesView: React.FC = () => {
  const [routines, setRoutines] = useState<DailyRoutine[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<null | {
    id?: string;
    dayOfWeek?: number;
  }>(null);
  const [importDraft, setImportDraft] = useState<ImportedDraftInput | null>(
    null,
  );
  const [importOpen, setImportOpen] = useState(false);
  const [workoutSession, setWorkoutSession] = useState<WorkoutSession | null>(
    null,
  );
  const [startingWorkout, setStartingWorkout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [moviendoId, setMoviendoId] = useState<string | null>(null);
  const { entrenados, marcarEntrenado } = useMiSemana();

  const load = useCallback(() => {
    setRoutines(null);
    setError(null);
    RoutineService.listMyRoutines()
      .then(setRoutines)
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const handleStartWorkout = async (id: string) => {
    setStartingWorkout(true);
    setError(null);
    try {
      const session = await WorkoutsService.start(id);
      setWorkoutSession(session);
      marcarEntrenado(diaDeHoy());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStartingWorkout(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar esta rutina? No podrás recuperarla.')) return;
    setDeletingId(id);
    try {
      await RoutineService.deleteMyRoutine(id);
      setRoutines((prev) => prev?.filter((r) => r.id !== id) ?? []);
      if (selectedId === id) setSelectedId(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  /**
   * Mueve una rutina a otro día, o la saca de la semana con `null`.
   *
   * El día solo admite una rutina: si ya había otra, el backend la deja sin
   * día. Por eso se recarga la lista entera en vez de parchear una fila — la
   * respuesta solo habla de la que se ha movido.
   */
  const colocarEn = async (id: string, dia: number | null) => {
    setMoviendoId(id);
    setError(null);
    try {
      await RoutineService.updateMyRoutine(id, { dayOfWeek: dia });
      const actualizadas = await RoutineService.listMyRoutines();
      setRoutines(actualizadas);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setMoviendoId(null);
    }
  };

  if (editing !== null || importDraft !== null) {
    const target = editing?.id
      ? routines?.find((r) => r.id === editing.id)
      : undefined;
    const diaNuevo = editing?.dayOfWeek;
    const cerrar = () => {
      setEditing(null);
      setImportDraft(null);
    };
    return (
      <RoutineEditor
        initialRoutine={target}
        initialDraft={importDraft ?? undefined}
        dayOfWeek={diaNuevo}
        heading={diaNuevo !== undefined ? NOMBRE_DEL_DIA[diaNuevo] : undefined}
        onCancel={cerrar}
        onCreated={() => {
          cerrar();
          load();
        }}
        onUpdated={() => {
          cerrar();
          load();
        }}
      />
    );
  }

  if (selectedId) {
    const selected = routines?.find((r) => r.id === selectedId);
    if (!selected) {
      setSelectedId(null);
      return null;
    }
    if (workoutSession) {
      return (
        <WorkoutView
          routine={selected}
          session={workoutSession}
          onFinish={() => setWorkoutSession(null)}
          onAbandon={() => setWorkoutSession(null)}
        />
      );
    }
    return (
      <div className="space-y-4">
        <button
          onClick={() => setSelectedId(null)}
          className="text-[#94a3b8] hover:text-white text-sm flex items-center gap-1.5"
        >
          ← Volver a mi semana
        </button>
        <RoutineView
          routine={selected}
          onStartWorkout={(r) => handleStartWorkout(r.id)}
          startingWorkout={startingWorkout}
        />
      </div>
    );
  }

  if (routines === null) {
    return (
      <div className="space-y-3">
        {ORDEN_DE_LA_SEMANA.map((d) => (
          <div
            key={d}
            className="bg-[#1e293b] rounded-xl border border-[#334155] h-20 animate-pulse"
          />
        ))}
      </div>
    );
  }

  const deEseDia = (dia: number) =>
    routines.find((r) => r.dayOfWeek === dia) ?? null;
  const sinColocar = routines.filter(
    (r) => r.dayOfWeek === null || r.dayOfWeek === undefined,
  );
  const colocadas = routines.length - sinColocar.length;
  const hechas = ORDEN_DE_LA_SEMANA.filter(
    (d) => deEseDia(d) !== null && entrenados.has(d),
  ).length;

  /**
   * Mover una rutina de día: las mismas flechas que para ver la del gimnasio.
   * El ciclo incluye «Sin día», que es como se saca de la semana.
   */
  const selectorDeDia = (r: DailyRoutine) => (
    <WeekdaySelector
      compact
      allowNone
      label={r.name}
      value={r.dayOfWeek ?? null}
      disabled={moviendoId === r.id}
      onChange={(dia) => void colocarEn(r.id, dia)}
    />
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-white text-lg font-semibold">Mi semana</h2>
          <p className="text-[#94a3b8] text-sm">
            {colocadas === 0
              ? 'Coloca una rutina en cada día que entrenes. Un día, una rutina.'
              : `${hechas} de ${colocadas} hechas esta semana. El lunes vuelven a estar por hacer.`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setImportOpen(true)}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-[#cbd5e1] bg-[#334155] hover:bg-[#475569]"
          >
            ⬆ Importar
          </button>
          <button
            onClick={() => setEditing({})}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#1f9e3f] hover:opacity-90"
          >
            + Crear rutina
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-900/40 border border-red-500/50 text-red-300 text-sm flex items-start gap-2">
          <span className="mt-0.5">
            <AlertIcon size={16} />
          </span>
          <span>{error}</span>
        </div>
      )}

      {importOpen && (
        <ImportRoutineModal
          onClose={() => setImportOpen(false)}
          onParsed={(draft) => {
            setImportOpen(false);
            setImportDraft(draft);
          }}
        />
      )}

      <ul className="space-y-3">
        {ORDEN_DE_LA_SEMANA.map((dia) => {
          const rutina = deEseDia(dia);
          const esHoy = dia === diaDeHoy();
          const hecha = rutina !== null && entrenados.has(dia);
          return (
            <li
              key={dia}
              className={`bg-[#1e293b] rounded-xl border p-4 ${
                esHoy ? 'border-[rgba(31,158,63,0.6)]' : 'border-[#334155]'
              }`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-[#64748b] flex items-center gap-2">
                    {NOMBRE_DEL_DIA[dia]} {numeroDelDia(dia)}
                    {esHoy && <span className="text-[#6ee06f]">· hoy</span>}
                    {hecha && <span className="text-[#6ee06f]">· hecha ✓</span>}
                  </p>
                  {rutina ? (
                    <>
                      <p className="text-white font-medium truncate">
                        {rutina.name}
                      </p>
                      <p className="text-[#94a3b8] text-xs mt-0.5">
                        {rutina.routineExercises?.length ?? 0} ejercicios
                        {rutina.estimatedDurationMinutes
                          ? ` · ${rutina.estimatedDurationMinutes} min`
                          : ''}
                      </p>
                    </>
                  ) : (
                    <p className="text-[#64748b] text-sm">Día libre</p>
                  )}
                </div>

                {rutina ? (
                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    {selectorDeDia(rutina)}
                    <button
                      onClick={() => {
                        setSelectedId(rutina.id);
                        void handleStartWorkout(rutina.id);
                      }}
                      disabled={startingWorkout}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#1f9e3f] hover:opacity-90 disabled:opacity-60"
                    >
                      Empezar
                    </button>
                    <button
                      onClick={() => setSelectedId(rutina.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#6ee06f] hover:bg-[#1f9e3f0a]"
                    >
                      Ver
                    </button>
                    <button
                      onClick={() => setEditing({ id: rutina.id })}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#cbd5e1] hover:bg-white/5"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => void handleDelete(rutina.id)}
                      disabled={deletingId === rutina.id}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#fca5a5] hover:bg-[rgba(220,38,38,0.15)] disabled:opacity-50"
                    >
                      {deletingId === rutina.id ? 'Eliminando…' : 'Eliminar'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditing({ dayOfWeek: dia })}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[#cbd5e1] bg-[#334155] hover:bg-[#475569]"
                  >
                    + Poner rutina
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {sinColocar.length > 0 && (
        <div className="bg-[#1e293b] rounded-xl border border-[#334155] p-4">
          <h3 className="text-white text-sm font-semibold mb-1">
            Sin colocar en la semana
          </h3>
          <p className="text-[#64748b] text-xs mb-3">
            Guardadas, pero sin día asignado. Elige uno y entran en la semana.
          </p>
          <ul className="space-y-2">
            {sinColocar.map((r) => (
              <li
                key={r.id}
                className="bg-[#172033] border border-[#334155] rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap"
              >
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {r.name}
                  </p>
                  <p className="text-[#94a3b8] text-xs">
                    {r.routineExercises?.length ?? 0} ejercicios
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                  {selectorDeDia(r)}
                  <button
                    onClick={() => setSelectedId(r.id)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#6ee06f] hover:bg-[#1f9e3f0a]"
                  >
                    Ver
                  </button>
                  <button
                    onClick={() => setEditing({ id: r.id })}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#cbd5e1] hover:bg-white/5"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => void handleDelete(r.id)}
                    disabled={deletingId === r.id}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#fca5a5] hover:bg-[rgba(220,38,38,0.15)] disabled:opacity-50"
                  >
                    {deletingId === r.id ? 'Eliminando…' : 'Eliminar'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MyRoutinesView;
